import React, { useEffect } from 'react'
import { Trans } from '@lingui/macro'
import { InputControl } from 'formik-chakra-ui'
import { staticLinks } from 'shared/linkBuilder'
import { useFormikContext } from 'formik'
import { useSelfSignupFormFieldsContext } from './SelfSignupFormFieldsContext'
import {
  AllowedUserFieldsEnum,
  AllowedUserFieldsSet
} from 'shared/accountNameConfig'
import { useTranslatedFieldsLabels } from './fields/useTranslatedFieldsLabels'
import {
  Box,
  ButtonPrimary,
  Heading,
  StackDivider,
  Text,
  VStack
} from 'front-end/src/components'
import { PasswordField } from './fields/PasswordField'
import { FormRow } from './fields/FormRow'
import { PrivacyPolicyCheckbox } from './fields/PrivacyPolicyCheckbox'
import {
  FIELDS_CUSTOM_INPUT,
  getUserFieldsProps,
  reduceSelfSignUpFields
} from 'front-end/src/pages/auth/AuthHelpers'
import { LoginContextUserType } from '../login/LoginContext'
import { brandColors } from 'shared/basicPalette'
import { Alert, AlertIcon, AlertTitle } from '@chakra-ui/react'

type SelfSignUpFormProps = {
  signupLoading: boolean
  token?: string
  defaultValues?: LoginContextUserType | null
  isAdditionalInfoForm?: boolean
  readOnly?: boolean
}

export const SelfSignUpForm: React.FC<SelfSignUpFormProps> = ({
  signupLoading,
  token,
  defaultValues,
  readOnly
}) => {
  const { fields, privacyPolicyUrl, isAdditionalInfoForm } =
    useSelfSignupFormFieldsContext()
  const { handleSubmit, isValid, setValues } = useFormikContext()

  useEffect(() => {
    if (defaultValues) {
      setValues(reduceSelfSignUpFields(defaultValues, fields))
    }
  }, [defaultValues])

  const formConstructorFields = React.useMemo(() => {
    return fields.filter((field) =>
      AllowedUserFieldsSet.has(field as string as AllowedUserFieldsEnum)
    )
  }, [fields])
  // we need to compute that constant on fly, cos translations need to be fetched before
  const FIELDS_LABELS = useTranslatedFieldsLabels()

  return (
    <form onSubmit={handleSubmit}>
      <Heading size="md">
        {isAdditionalInfoForm ? (
          <Trans>Additional information</Trans>
        ) : (
          <Trans>Create your account</Trans>
        )}
      </Heading>
      <Text mt="12px">
        <Trans>We need to know more information about you. </Trans>
      </Text>
      <VStack
        divider={<StackDivider bg="gray.200" h="1px" />}
        spacing={'10px'}
        align="stretch"
      >
        <Box>
          {readOnly && formConstructorFields.length > 0 && (
            <Alert status="warning" flexWrap={['wrap', 'initial']} mt="24px">
              <AlertIcon />
              <AlertTitle mr="8px">
                <Trans>
                  Current configuration does not allow you to edit your
                  information, please contact your administrator
                </Trans>
              </AlertTitle>
            </Alert>
          )}
          {formConstructorFields.map((field) => {
            return (
              <FormRow key={field}>
                {FIELDS_CUSTOM_INPUT[field] ? (
                  FIELDS_CUSTOM_INPUT[field]({ isReadOnly: readOnly })
                ) : (
                  <InputControl
                    {...getUserFieldsProps(field, FIELDS_LABELS)}
                    isReadOnly={readOnly}
                  />
                )}
              </FormRow>
            )
          })}
        </Box>
        <Box>
          {!isAdditionalInfoForm && <PasswordField />}
          {!!privacyPolicyUrl && (
            <PrivacyPolicyCheckbox policyUrl={privacyPolicyUrl} />
          )}
        </Box>
      </VStack>
      <ButtonPrimary
        disabled={!isValid}
        type="submit"
        isFullWidth
        mt="12px"
        isLoading={signupLoading}
      >
        <Trans>Next</Trans>
      </ButtonPrimary>

      <Box className="bottomLink">
        <Box
          color={brandColors.grey['700']}
          as={'a'}
          href={`${staticLinks.login}/${token ? token : ''}`}
        >
          <Trans>Cancel</Trans>
        </Box>
      </Box>
    </form>
  )
}
